import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Eye, Loader2, MessageSquareText, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/admin/PageHeader";
import { Toolbar, Segmented } from "@/components/admin/Toolbar";
import { EmptyState } from "@/components/admin/states/EmptyState";
import { ErrorState } from "@/components/admin/states/ErrorState";
import { adminPhotoComments, adminSetPhotoCommentStatus, errorMessage, imgUrl } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";

const STATUS = { new: ["badge-warning", "Ny"], read: ["badge-muted", "Lesen"], handled: ["badge-confirmed", "Handtert"] };

/** One private note: the picture, its number, the words exactly as written (rendered as text – never as HTML), who/when, and the next step. */
const CommentCard = ({ c, onStatus, busy }) => {
  const [cls, label] = STATUS[c.status] || STATUS.new;
  return (
    <li className={cn("admin-card flex gap-4 p-4", c.status === "new" && "border-l-4 border-l-sol")} data-testid={`photo-comment-${c.id}`} data-status={c.status}>
      <a href={c.src} target="_blank" rel="noreferrer" className="block h-20 w-28 shrink-0 overflow-hidden rounded-[10px] bg-secondary" aria-label={`Opne bilete nr. ${c.number}`} data-testid={`photo-comment-thumb-${c.id}`}>
        <img src={imgUrl(c.src, 480)} alt="" className="h-full w-full object-cover" loading="lazy" />
      </a>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="font-mono text-[12px] font-semibold tabular tracking-[0.04em]" data-testid={`photo-comment-number-${c.id}`}>Bilete nr. {c.number}</span>
          <span className={cn("badge", cls)} data-testid={`photo-comment-status-${c.id}`}>{label}</span>
          <span className="text-[12px] text-muted-foreground tabular">{fmtDateTime(c.created_at)}</span>
          {c.name && <span className="text-[12px] text-muted-foreground">frå <span className="text-foreground" data-testid={`photo-comment-name-${c.id}`}>{c.name}</span></span>}
          {c.page && <span className="truncate text-[12px] text-muted-foreground" title={c.page}>på {c.page}</span>}
        </div>
        <p className="mt-2 whitespace-pre-wrap break-words text-[14.5px] leading-relaxed" data-testid={`photo-comment-text-${c.id}`}>{c.text}</p>
        {c.handled_at && <p className="mt-1 text-[12px] text-muted-foreground">Handtert {fmtDateTime(c.handled_at)}{c.handled_by ? ` av ${c.handled_by}` : ""}</p>}
        <div className="mt-3 flex flex-wrap gap-2">
          {c.status === "new" && <Button size="sm" variant="outline" className="h-8 bg-card text-[12.5px]" onClick={() => onStatus(c.id, "read")} disabled={busy === c.id} data-testid={`photo-comment-read-${c.id}`}><Eye className="h-3.5 w-3.5" /> Merk som lesen</Button>}
          {c.status !== "handled" && <Button size="sm" className="h-8 text-[12.5px]" onClick={() => onStatus(c.id, "handled")} disabled={busy === c.id} data-testid={`photo-comment-handle-${c.id}`}>{busy === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Handtert</Button>}
          {c.status === "handled" && <Button size="sm" variant="ghost" className="h-8 text-[12.5px]" onClick={() => onStatus(c.id, "read")} disabled={busy === c.id} data-testid={`photo-comment-reopen-${c.id}`}><RotateCcw className="h-3.5 w-3.5" /> Opne att</Button>}
        </div>
      </div>
    </li>
  );
};

export default function AdminPhotoComments() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("open");
  const [busy, setBusy] = useState(null);
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: ["admin-photo-comments", filter], queryFn: () => adminPhotoComments(filter) });
  const set = useMutation({
    mutationFn: ({ id, status }) => adminSetPhotoCommentStatus(id, status),
    onMutate: ({ id }) => setBusy(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-photo-comments"] }); qc.invalidateQueries({ queryKey: ["admin-dashboard"] }); },
    onError: (e) => toast.error(errorMessage(e, "Kunne ikkje oppdatere")),
    onSettled: () => setBusy(null),
  });
  const counts = data?.counts || {};
  const items = data?.items || [];
  return (
    <div>
      <PageHeader title="Bildekommentarar" description="Private merknader folk har skrive til eit bestemt bilete på nettsida – t.d. kven som er med, kvar og når. Dei vert aldri viste offentleg; berre her. Ingen varsel går ut." />
      <Toolbar count={items.length} countLabel="kommentarar" testId="photo-comments-toolbar">
        <Segmented size="sm" value={filter} onChange={setFilter} items={[{ value: "open", label: "Nye og lesne", count: (counts.new || 0) + (counts.read || 0) }, { value: "new", label: "Nye", count: counts.new }, { value: "handled", label: "Handterte", count: counts.handled }, { value: "all", label: "Alle" }]} testId="photo-comments-filter" />
      </Toolbar>
      {isError ? <ErrorState title="Kunne ikkje hente kommentarane" text={error?.message} onRetry={refetch} /> : isLoading ? (
        <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
      ) : !items.length ? (
        <div className="admin-card"><EmptyState compact icon={MessageSquareText} title={filter === "open" ? "Ingen nye kommentarar" : "Ingen kommentarar her"} text="Når nokon kommenterer eit bilete frå biletvisaren på nettsida, dukkar det opp her." testId="photo-comments-empty" /></div>
      ) : (
        <ul className="space-y-3" data-testid="photo-comments-list">
          {items.map((c) => <CommentCard key={c.id} c={c} busy={busy} onStatus={(id, status) => set.mutate({ id, status })} />)}
        </ul>
      )}
    </div>
  );
}
